import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Command } from "cmdk";
import {
  ArrowRight,
  Building2,
  Loader2,
  Megaphone,
  RadioTower,
  Search,
  Target,
  UserRound,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Admin, AdminSearchItem, AdminSearchKind } from "@lumika/shared";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  cn,
} from "@lumika/ui";
import { adminApi } from "../api/admin";

const RESULT_LIMIT = 5;

const KIND_ICONS: Record<AdminSearchKind, LucideIcon> = {
  route: ArrowRight,
  dealership: Building2,
  contact: UserRound,
  lead: Target,
  telephony_account: RadioTower,
  outreach_prospect: Megaphone,
};

export function AdminGlobalSearch({
  role,
  variant = "desktop",
}: {
  role: Admin["role"];
  variant?: "desktop" | "mobile";
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(timeout);
  }, [query]);

  useEffect(() => {
    if (variant !== "desktop") return;
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    document.addEventListener("keydown", openSearch);
    return () => document.removeEventListener("keydown", openSearch);
  }, [variant]);

  useEffect(() => {
    if (open) return;
    setQuery("");
    setDebounced("");
  }, [open]);

  const resultsQuery = useQuery({
    queryKey: ["admin", "global-search", role, debounced],
    queryFn: () => adminApi.globalSearch({ q: debounced, limit: RESULT_LIMIT }),
    enabled: open,
    staleTime: 30_000,
  });

  const groups = resultsQuery.data?.groups ?? [];
  const itemCount = groups.reduce((sum, group) => sum + group.items.length, 0);
  const isDebouncing = query.trim() !== debounced;

  function select(item: AdminSearchItem) {
    setOpen(false);
    navigate(item.href);
  }

  return (
    <>
      {variant === "desktop" ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Search admin (⌘K)"
          className={cn(
            "hidden min-h-touch w-full items-center gap-2 rounded-md border border-input bg-muted/40 px-3 text-sm text-muted-foreground md:flex",
            "transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="flex-1 text-left">Search admin…</span>
          <kbd className="rounded border bg-background px-1.5 py-0.5 text-[10px] font-medium">
            ⌘K
          </kbd>
        </button>
      ) : (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Search admin"
          onClick={() => setOpen(true)}
          className="md:hidden"
        >
          <Search className="h-5 w-5" />
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          bare
          aria-describedby={undefined}
          className={cn(
            "overflow-hidden sm:top-[18%] sm:max-w-2xl sm:translate-y-0 sm:rounded-xl",
            "sm:data-[state=open]:!slide-in-from-top-2 sm:data-[state=closed]:!slide-out-to-top-2",
          )}
        >
          <DialogTitle className="sr-only">Search Lumika Admin</DialogTitle>
          <Command
            label="Search dealerships, contacts, leads, accounts, and routes"
            shouldFilter={false}
            className="flex min-h-0 flex-col"
          >
            <div className="flex min-h-touch items-center gap-3 border-b px-4">
              <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                aria-label="Search dealerships, contacts, leads, accounts, and routes"
                placeholder="Search dealerships, contacts, leads, accounts, and routes…"
                autoComplete="off"
                maxLength={120}
                spellCheck={false}
                className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground sm:text-sm"
              />
              {resultsQuery.isFetching || isDebouncing ? (
                <Loader2
                  aria-label="Searching"
                  className="h-4 w-4 shrink-0 animate-spin text-muted-foreground motion-reduce:hidden"
                />
              ) : null}
              <DialogClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Close search"
                  className="-mr-2 shrink-0 sm:hidden"
                >
                  <X className="h-4 w-4" />
                </Button>
              </DialogClose>
            </div>

            <Command.List className="max-h-[min(28rem,70dvh)] overflow-y-auto overflow-x-hidden p-2">
              {isDebouncing ? (
                <p role="status" className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Searching…
                </p>
              ) : resultsQuery.isError ? (
                <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                  <p className="text-sm text-destructive">Search is unavailable.</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void resultsQuery.refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : resultsQuery.isLoading ? (
                <p role="status" className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading commands…
                </p>
              ) : (
                <>
                  {groups.map((group) => (
                    <Command.Group
                      key={group.kind}
                      heading={group.label}
                      className="mb-2 last:mb-0 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
                    >
                      {group.items.map((item) => (
                        <SearchResult
                          key={`${item.kind}:${item.id}`}
                          item={item}
                          onSelect={select}
                        />
                      ))}
                      {group.isTruncated ? (
                        <p className="px-2 py-1.5 text-xs text-muted-foreground">
                          Showing {group.items.length} of {group.total}. Refine your search
                          to see the rest.
                        </p>
                      ) : null}
                    </Command.Group>
                  ))}

                  {itemCount === 0 ? (
                    <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {debounced.length < 2
                        ? "Type at least 2 characters to search records."
                        : `No results for “${debounced}”.`}
                    </p>
                  ) : debounced.length === 1 ? (
                    <p className="px-3 py-2 text-xs text-muted-foreground">
                      Type one more character to search records.
                    </p>
                  ) : null}
                </>
              )}
            </Command.List>

            <div className="hidden items-center justify-between border-t px-4 py-2 text-xs text-muted-foreground sm:flex">
              <span>↑↓ Move · Enter Open · Esc Close</span>
              <span>{role === "readonly" ? "Read-only access" : "Platform admin"}</span>
            </div>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
}

function SearchResult({
  item,
  onSelect,
}: {
  item: AdminSearchItem;
  onSelect: (item: AdminSearchItem) => void;
}) {
  const Icon = KIND_ICONS[item.kind];
  return (
    <Command.Item
      value={`${item.kind}:${item.id}`}
      onSelect={() => onSelect(item)}
      className={cn(
        "flex min-h-touch cursor-default select-none items-center gap-3 rounded-md px-2.5 py-2 text-sm outline-none",
        "data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground",
      )}
    >
      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{item.label}</span>
        <span className="block truncate text-xs text-muted-foreground">
          {item.detail}
        </span>
      </span>
      <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" />
    </Command.Item>
  );
}
